import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useProducts, useCategories, money, imgUrl } from "./useProducts";

export const Categories = () => {
  const products = useProducts();
  const tiles = useCategories().map((c) => {
    const items = products.filter((p) => p.category === c.id);
    const cover = c.cover ? { image: c.cover, position: c.cover_position || "object-center" } : items[0];
    const from = items.length ? Math.min(...items.flatMap((p) => p.options.map((o) => o.price))) : null;
    return { ...c, items, cover, from };
  });

  return (
    <section data-testid="product-showcase-section" className="relative py-24 sm:py-32 bg-[#f3f2ec]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-4 flex items-center gap-3">
              <span className="diamond" /> Shop Drop 001
            </p>
            <h2 className="font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              The <span className="text-stroke">Collection</span>
            </h2>
          </div>
          <Link data-testid="shop-all-link" to="/shop" className="group inline-flex items-center gap-2 font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#0a0a0b] hover:text-[#ff2a85] transition-colors">
            View all pieces <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/shop/${t.id}`}
                data-testid={`category-tile-${t.id}`}
                className="group relative block overflow-hidden rounded-2xl border border-[#e3e2dc] bg-[#faf9f6] hover:border-[#ff2a85]/50 transition-colors duration-500"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#f3f2ec]">
                  {t.cover ? (
                    <img src={imgUrl(t.cover.image)} alt={t.label} className={`absolute inset-0 w-full h-full object-cover ${t.cover.position || "object-top"} group-hover:scale-[1.04] transition-transform duration-700 ease-out`} />
                  ) : (
                    <div className="absolute inset-0 bg-[#e3e2dc]" />
                  )}
                  <span className="absolute top-4 left-4 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#ff2a85] bg-[#ffffff]/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#ff2a85]/30">
                    {t.items.length} {t.items.length === 1 ? "piece" : "pieces"}
                  </span>
                </div>
                <div className="p-4 sm:p-5 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display font-extrabold uppercase text-lg sm:text-2xl leading-tight">{t.label}</h3>
                    {t.from != null && <p className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#5f5e5a] mt-1">from {money(t.from)}</p>}
                  </div>
                  <span className="h-9 w-9 shrink-0 rounded-full bg-[#0a0a0b] text-white flex items-center justify-center group-hover:bg-[#ff2a85] transition-colors">
                    <ArrowUpRight size={15} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
